import { supabase } from "../supabase/SupabaseClient";
import { TOPE_PLANILLA_MB } from "../shared/topes";
import { centrarLoEscrito } from "../shared/centrarPlanillas";

/* Mi espacio: las planillas personales de cada usuario.
 *
 * La RLS hace todo el trabajo de privacidad: `dueno = mi_mail()`, así que estas
 * consultas ya vuelven SÓLO las del que está logueado, sin filtrar a mano. El
 * `dueno` lo pone la base sola (default `mi_mail()`) al crear. Nosotros nos
 * alimentamos de esto desde el lado del servidor (service_role, que saltea RLS).
 */

/* El mail del que está logueado (para separar lo propio de lo compartido y
   saber si una planilla es de solo lectura). Sale de la sesión, no de la red. */
export async function miMail() {
  const { data } = await supabase.auth.getSession();
  return (data?.session?.user?.email || "").toLowerCase();
}

/* La lista, sin el contenido. Con la RLS, vuelven TANTO las mías como las que me
   compartieron; el `dueno` deja separarlas en la pantalla. Lo de la papelera
   no: las compartidas ya no vienen (la RLS de los invitados lo pide), pero las
   MÍAS borradas sí —el dueño las tiene que poder ver para restaurarlas—, así
   que se sacan acá. Las lista `planillasEnPapelera`. `editores` es para
   que «Compartidas conmigo» diga si puedo editar o sólo ver. */
export async function misPlanillas() {
  const { data, error } = await supabase
    .from("espacio_planillas")
    .select("id, nombre, dueno, editores, actualizado_en")
    .is("borrada_en", null)
    .order("actualizado_en", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* Una planilla entera, con su grid, su dueño y con quién está compartida. El
   `actualizado_en` es la «versión» que la pestaña tiene en la mano: el
   guardado lo lleva para no pisar lo que otro escribió (ver guardarPlanilla).
   `editores` (los invitados que pueden editar) y `actualizado_por` (quién
   guardó lo último, para el cartel del «en vivo»). */
export async function traerPlanilla(id) {
  const { data, error } = await supabase
    .from("espacio_planillas")
    .select("id, nombre, datos, dueno, compartida_con, editores, actualizado_en, actualizado_por")
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

/* La guía interna: los compañeros con los que se puede compartir (mail+nombre). */
export async function genteParaCompartir() {
  const { data, error } = await supabase.rpc("gente_para_compartir");
  if (error) throw error;
  return data || [];
}

/* Guarda con quién está compartida una planilla (sólo el dueño; la RLS lo exige).
   NO toca `actualizado_en`: es la versión del CONTENIDO, y si compartir la
   moviera, la propia pestaña abierta chocaría consigo misma al guardar la
   próxima letra. Compartir no pisa nada: sólo escribe `compartida_con`.
 *
 * `editores` (FiboSheets #6): de esos, los que además pueden EDITAR. Va sólo
 * si se pasa —sin él, se comparte como antes y la base deja los editores
 * que sigan compartidos—, y siempre recortado a `mails`: un editor que ya no
 * está compartido no edita (la base lo poda igual, esto es para no mandar
 * algo que se va a tirar). */
export async function guardarCompartir(id, mails, editores) {
  const cambios = { compartida_con: mails };
  if (Array.isArray(editores)) {
    const con = new Set(mails);
    cambios.editores = editores.filter((m) => con.has(m));
  }
  const { error } = await supabase
    .from("espacio_planillas")
    .update(cambios)
    .eq("id", id);
  if (error) throw error;
}

/* ── En vivo (FiboSheets #6) ─────────────────────────────────────────────────
 *
 * Con la planilla abierta, cada vez que alguien la guarda (un editor, el
 * dueño en otra pestaña, el bot) llega la fila nueva por Supabase Realtime:
 * la tabla está en la publicación `supabase_realtime` y el realtime aplica
 * la RLS de lectura, así que le llega sólo a quien la puede ver. Lo que se
 * hace con eso (recargar o avisar) lo decide la pantalla
 * (routes/usePlanillaEnVivo.js); acá sólo se escucha. El canal lleva un
 * número para que dos pestañas del mismo editor (o el editor montado dos
 * veces en un test) no compartan canal. Devuelve con qué dejar de escuchar. */
let canalesPlanilla = 0;
export function alCambiarPlanilla(id, alCambiar) {
  const canal = supabase
    .channel(`planilla:${id}:${(canalesPlanilla += 1)}`)
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "espacio_planillas", filter: `id=eq.${id}` },
      (payload) => alCambiar(payload.new || {}),
    )
    .subscribe();
  return () => { supabase.removeChannel(canal); };
}

/* Por qué no se pudo guardar, para que la pantalla diga la verdad. */
export const CONFLICTO = "conflicto"; // alguien guardó en el medio
export const MUY_GRANDE = "muy_grande"; // pasó el tope de la base (TOPE_PLANILLA_MB)

function errorDeGuardado(codigo, mensaje, causa = null) {
  const e = new Error(mensaje);
  e.codigo = codigo;
  if (causa) {
    e.causa = causa;
    // El código de Postgres sigue a mano para quien ya lo miraba (las
    // importaciones del bot reconocen el 23514).
    if (causa.code) e.code = causa.code;
  }
  return e;
}

/* 23514: la CHECK `espacio_planillas_datos_chicos` (datos < TOPE_PLANILLA_MB). */
const pasaElTope = (error) => error.code === "23514" || /datos_chicos/.test(error.message || "");

/* Crea una y devuelve su id. `dueno` lo pone la base. Si `datos` pasa el
   tope de la base (un Excel importado muy grande) tira MUY_GRANDE, igual que
   `guardarPlanilla`. */
export async function crearPlanilla(nombre, datos) {
  const { data, error } = await supabase
    .from("espacio_planillas")
    .insert({ nombre, datos })
    .select("id")
    .single();
  if (error) {
    if (pasaElTope(error)) throw errorDeGuardado(MUY_GRANDE, `La planilla pasa el tope de ${TOPE_PLANILLA_MB} MB`, error);
    throw error;
  }
  return data.id;
}

/* Guarda nombre + grid, con escritura OPTIMISTA (lo mismo que hace el bot en
 * `guardar_planilla` de automatizaciones.py): el UPDATE sólo matchea si
 * `actualizado_en` sigue siendo el que esta pestaña leyó o guardó por última
 * vez. Si una automatización (o otra pestaña) escribió en el medio, no
 * matchea ninguna fila y se tira un error CONFLICTO en vez de pisarla con la
 * copia vieja. Devuelve el `actualizado_en` nuevo: la versión para el próximo
 * guardado. El reloj lo pone el cliente (no hay trigger en la tabla), igual
 * que el bot; lo que se compara es el texto que vuelve de la base, tal cual.
 *
 * `{ pisar: true }` saltea la comparación: es el «guardar mi versión igual»
 * que la persona elige a conciencia después de un conflicto. */
export async function guardarPlanilla(id, nombre, datos, actualizadoEn, { pisar = false } = {}) {
  // ONLY BITRENES y ARRIBOS Y CORTES: lo escrito, centrado (ticket 546).
  const aGuardar = centrarLoEscrito(nombre, datos);
  let q = supabase
    .from("espacio_planillas")
    .update({ nombre, datos: aGuardar, actualizado_en: new Date().toISOString() })
    .eq("id", id);
  if (!pisar) {
    q = actualizadoEn ? q.eq("actualizado_en", actualizadoEn) : q.is("actualizado_en", null);
  }
  const { data, error } = await q.select("actualizado_en");
  if (error) {
    if (pasaElTope(error)) {
      throw errorDeGuardado(MUY_GRANDE, `La planilla pasó el tope de ${TOPE_PLANILLA_MB} MB`, error);
    }
    throw error;
  }
  if (!data || !data.length) {
    throw errorDeGuardado(CONFLICTO, "La planilla cambió en otro lado");
  }
  return data[0].actualizado_en;
}

/* ── La papelera ─────────────────────────────────────────────────────────────
 *
 * Borrar ya no es para siempre: la planilla va a la papelera (`borrada_en`) y
 * el dueño la restaura o la borra de verdad. A los PAPELERA_DIAS la borra sola
 * una tarea de pg_cron en la base (`purgar_papelera_planillas`), abra o no
 * alguien la papelera. Ver 20261005090000_espacio_papelera_historial.sql. */
export const PAPELERA_DIAS = 30;

/* «Borrar planilla»: la manda a la papelera. Como `guardarCompartir`, NO toca
   `actualizado_en` (es la versión del contenido) ni `datos`. Para los
   invitados desaparece en el acto; la RLS sólo deja hacerlo al dueño. */
export async function borrarPlanilla(id) {
  const { error } = await supabase
    .from("espacio_planillas")
    .update({ borrada_en: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

/* Lo mío que está en la papelera, lo último que borré primero. La RLS ya no
   le muestra a un invitado una planilla borrada, así que esto trae sólo las
   del que mira. */
export async function planillasEnPapelera() {
  const { data, error } = await supabase
    .from("espacio_planillas")
    .select("id, nombre, dueno, borrada_en, actualizado_en")
    .not("borrada_en", "is", null)
    .order("borrada_en", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* Sacarla de la papelera: vuelve a la lista (y a los que la tenían compartida). */
export async function restaurarDePapelera(id) {
  const { error } = await supabase
    .from("espacio_planillas")
    .update({ borrada_en: null })
    .eq("id", id);
  if (error) throw error;
}

/* Borrarla de verdad (con su historial, que se va en cascada). Sólo desde la
   papelera: el filtro por `borrada_en` hace que un id equivocado no se lleve
   puesta una planilla viva. */
export async function borrarParaSiempre(id) {
  const { error } = await supabase
    .from("espacio_planillas")
    .delete()
    .eq("id", id)
    .not("borrada_en", "is", null);
  if (error) throw error;
}

/* ── El historial de versiones ───────────────────────────────────────────────
 *
 * Las fotos las saca la base sola (un trigger al cambiar `datos`, como mucho
 * una cada 10 minutos, también cuando escribe el bot) y el cliente sólo las
 * LEE: no hay INSERT/UPDATE/DELETE para `authenticated`. La foto guarda cómo
 * estaba la planilla ANTES del cambio: `guardada_en`/`guardada_por` son quién
 * y cuándo la dejó así. Las ve quien ve la planilla (la misma RLS). */

/* La lista, sin el contenido (cada una puede pesar hasta TOPE_PLANILLA_MB). */
export async function versionesDePlanilla(planillaId) {
  const { data, error } = await supabase
    .from("espacio_planillas_versiones")
    .select("id, nombre, guardada_en, guardada_por, creada_en")
    .eq("planilla_id", planillaId)
    .order("creada_en", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* Una versión entera, para verla o restaurarla. */
export async function traerVersion(versionId) {
  const { data, error } = await supabase
    .from("espacio_planillas_versiones")
    .select("id, planilla_id, nombre, datos, guardada_en, guardada_por")
    .eq("id", versionId)
    .single();
  if (error) throw error;
  return data;
}

/* Saca ya una foto de lo de AHORA al Historial, sin esperar los 10 minutos
 * del trigger: para que lo que se escribe después se pueda deshacer
 * restaurando esa foto. Si la de recién ya es igual, la base no repite.
 * Tira el error si no se pudo (el que escribe después no debería seguir). */
export async function guardarVersion(planillaId) {
  const { error } = await supabase.rpc("guardar_version_planilla", { p_planilla: planillaId });
  if (error) throw error;
}

/* Vuelve una versión vieja la actual. Dos pasos:
 *  1. `guardar_version_planilla`: foto de lo de AHORA, sin esperar los 10
 *     minutos del trigger. Es lo que permite deshacer: restaurar esa foto.
 *  2. `guardarPlanilla` con el `actualizado_en` que la pestaña tiene en la
 *     mano: si alguien (otra pestaña, una automatización) escribió en el
 *     medio, es CONFLICTO y no se pisa nada. El nombre queda el de ahora: se
 *     restaura el contenido.
 * Devuelve el `actualizado_en` nuevo. Sólo el dueño (la base lo exige). */
export async function restaurarVersion(planillaId, versionId, { nombre, actualizadoEn }) {
  const v = await traerVersion(versionId);
  if (!v || v.planilla_id !== planillaId) throw new Error("Esa versión no es de esta planilla");
  await guardarVersion(planillaId);
  return guardarPlanilla(planillaId, nombre, v.datos, actualizadoEn);
}

/* ── Documentos de texto libre (la otra mitad de Mi espacio) ────────────────── */

export async function misDocumentos() {
  const { data, error } = await supabase
    .from("espacio_documentos")
    .select("id, titulo, dueno, actualizado_en")
    .order("actualizado_en", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function traerDocumento(id) {
  const { data, error } = await supabase
    .from("espacio_documentos")
    .select("id, titulo, contenido, dueno, compartida_con")
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

/* Con quién está compartido un documento (sólo el dueño; la RLS lo exige). */
export async function guardarCompartirDoc(id, mails) {
  const { error } = await supabase
    .from("espacio_documentos")
    .update({ compartida_con: mails, actualizado_en: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function crearDocumento(titulo) {
  const { data, error } = await supabase
    .from("espacio_documentos")
    .insert({ titulo })
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

export async function guardarDocumento(id, titulo, contenido) {
  const { error } = await supabase
    .from("espacio_documentos")
    .update({ titulo, contenido, actualizado_en: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function borrarDocumento(id) {
  const { error } = await supabase.from("espacio_documentos").delete().eq("id", id);
  if (error) throw error;
}

/* ── Pendientes: la ticketera personal (una fila por tarea) ──────────────────── */

const COLS_TAREA = "id, texto, hecho, nota, creado_en, hecho_en";
/* Las que te dejó otro (Nico, 06/10: «si lo derivo a Andy, que aparezca en la
   ticketera de Andy»): el «Derivar a…» de Avisos de ruta las inserta desde el
   bot con estas columnas (migración 20261007000000). */
const COLS_ORIGEN = "origen, origen_ref, origen_por, foto_url, origen_url";
/* Las que me anotó otro y sólo él da por terminadas (Nico, 20/10; migración
   20261020210100): quién la dio (`dado_por`, contra mi `dueno`) y si ya dije
   «la terminé» (`lista_en`). */
const COLS_DADA = "dueno, dado_por, lista_en";

/* 42703 = la columna no existe (Postgres); PGRST204 = PostgREST no la ve. */
const faltaColumna = (error) =>
  !!error && (error.code === "42703" || error.code === "PGRST204" || /column/i.test(error.message || ""));

/* Todo lo mío, de una. La RLS ya recorta a "lo tuyo"; la pantalla las separa en
   pendientes y hechas. Traigo lo más viejo primero para respetar el orden en que
   se fueron anotando.
 *
 * Con las columnas nuevas si existen; si una migración todavía no está
 * aplicada, la consulta falla por la columna y se lee con lo que haya, hasta
 * llegar a como era al principio: la pantalla no se cae por una columna que
 * no está. Primero sin las de «la que me dio otro» (lo que hoy tiene
 * producción), después sin las de origen. */
export async function misTareas() {
  const leer = (cols) =>
    supabase.from("espacio_tareas").select(cols).order("creado_en", { ascending: true });
  const juegos = [`${COLS_TAREA}, ${COLS_ORIGEN}, ${COLS_DADA}`, `${COLS_TAREA}, ${COLS_ORIGEN}`, COLS_TAREA];
  let data;
  let error;
  for (const cols of juegos) {
    ({ data, error } = await leer(cols));
    if (!faltaColumna(error)) break;
  }
  if (error) throw error;
  return data || [];
}

/* ── Las que le di a otro (Nico, 20/10) ──────────────────────────────────────
 *
 * «Le di una tarea a Ruben y quiero controlarla en el futuro. Y que sólo yo la
 * pueda dar por finalizada.» La tarea vive en los pendientes del otro y la RLS
 * no me deja leerla (es suya): lo de quien la dio pasa por tres funciones de
 * la base que hacen lo justo (migración 20261020210100).
 *
 * Sin la migración la función no existe: `tareasQueDi` devuelve null y la
 * pantalla no muestra la parte de «Las que di», como hoy. */

/* PGRST202 = PostgREST no encuentra la función; 42883 = Postgres tampoco. */
const faltaFuncion = (error) =>
  !!error && (error.code === "PGRST202" || error.code === "42883" ||
              /could not find the function|function .* does not exist/i.test(error.message || ""));

/* Las que anoté para otros, con para quién (`dueno`), la nota, desde cuándo y
   en qué están. `null` = todavía no se puede (falta la migración). */
export async function tareasQueDi() {
  const { data, error } = await supabase.rpc("tareas_que_di");
  if (faltaFuncion(error)) return null;
  if (error) throw error;
  return data || [];
}

/* Quien la dio: le da el OK (`cerrar` true, queda hecha para los dos) o la
   reabre (false: vuelve a pendiente y se borra el «la terminé»). */
export async function cerrarTareaDada(id, cerrar = true) {
  const { error } = await supabase.rpc("cerrar_tarea_dada", { p_id: id, p_cerrar: !!cerrar });
  if (error) throw error;
}

/* Quien la dio: la retira de los pendientes del otro. */
export async function retirarTareaDada(id) {
  const { error } = await supabase.rpc("retirar_tarea_dada", { p_id: id });
  if (error) throw error;
}

/* El que la recibió: «la terminé» (o «todavía no»). No la cierra: le muestra
   a quien la dio que falta su OK. La base no le deja tocar `hecho`. */
export async function marcarTerminada(id, terminada) {
  const { error } = await supabase
    .from("espacio_tareas")
    .update({ lista_en: terminada ? new Date().toISOString() : null })
    .eq("id", id);
  if (error) throw error;
}

/* El chofer detrás de cada tarea derivada: `{ [id del aviso]: { numero,
 * quien, rol } }`.
 *
 * Nico, 09/10: «en cualquiera [reporte] que venga de parte de un chofer […]
 * que aparezca el número del chofer clickeable». Lo que te derivan desde
 * Avisos de ruta es eso —«Foto de un chofer: Roberto Carlos Aguilera…»— y el
 * número no viaja en la tarea: está en el aviso (`avisos_ruta.numero`, el
 * WhatsApp desde el que escribió, 74 de 74 con 549 + diez dígitos). Así que
 * una consulta más, por los ids de `origen_ref`, de una sola vez.
 *
 * La usa también la ticketera, para el mismo aviso cuando el «Derivar a…» lo
 * convirtió en ticket (`avisoDelTicket`). Las dos pantallas la llaman SÓLO si
 * quien mira tiene `ver.telefono_chofer`: para el resto no sale ni la
 * consulta. NUNCA TIRA: si no se puede leer, la tarea o el ticket se ven como
 * siempre, sin el número. Es un atajo, no un dato que falte. */
export async function choferesDeAvisos(ids) {
  const limpios = [...new Set((ids || []).map((v) => String(v ?? "").trim()))].filter((v) =>
    /^\d+$/.test(v),
  );
  if (!limpios.length) return {};
  try {
    const { data, error } = await supabase
      .from("avisos_ruta")
      .select("id, numero, quien, rol")
      .in("id", limpios);
    if (error) {
      console.warn("[whatsapp-chofer] no pude leer los avisos:", error.message);
      return {};
    }
    const out = {};
    for (const a of data || []) {
      out[String(a.id)] = { numero: a.numero || "", quien: a.quien || "", rol: a.rol || "" };
    }
    return out;
  } catch (e) {
    return {};
  }
}

/* Anota una tarea nueva y devuelve la fila creada (para pintarla sin recargar). */
export async function crearTarea(texto) {
  const { data, error } = await supabase
    .from("espacio_tareas")
    .insert({ texto })
    .select(COLS_TAREA)
    .single();
  if (error) throw error;
  return data;
}

/* Anotarle algo a otra persona (o al sector), Nico #385/#387: «que te
 * permita decidir si es personal o del sector». La RLS no deja escribir en
 * los pendientes de otro, así que pasa por `asignar_pendiente` (migración
 * 20261015222401), que devuelve `{ tipo: "tarea" | "ticket", id, area,
 * etiqueta, para }`. Sin `para`, es para uno mismo; con `ambito` "sector" y
 * sin `area`, el área de la ficha de esa persona (o error `sin_sector`). */
export async function asignarPendiente({ texto, para = "", ambito = "personal", area = "" }) {
  const { data, error } = await supabase.rpc("asignar_pendiente", {
    p_texto: texto,
    p_para: para || null,
    p_ambito: ambito,
    p_area: area || null,
  });
  if (error) throw error;
  return data || {};
}

/* Tilda / destilda. Al tildar queda la marca de cuándo se hizo. */
export async function marcarTarea(id, hecho) {
  const { error } = await supabase
    .from("espacio_tareas")
    .update({ hecho, hecho_en: hecho ? new Date().toISOString() : null })
    .eq("id", id);
  if (error) throw error;
}

export async function editarTarea(id, texto) {
  const { error } = await supabase
    .from("espacio_tareas")
    .update({ texto })
    .eq("id", id);
  if (error) throw error;
}

/* La anotación libre de una tarea (Nico, 28/09): notas a mano, aparte del texto. */
export async function anotarTarea(id, nota) {
  const { error } = await supabase
    .from("espacio_tareas")
    .update({ nota })
    .eq("id", id);
  if (error) throw error;
}

export async function borrarTarea(id) {
  const { error } = await supabase.from("espacio_tareas").delete().eq("id", id);
  if (error) throw error;
}

/* Los Excel que llegaron al bot para Mis planillas (FiboSheets #10). Viven en
   su archivo y se reexportan acá: quien mockea este servicio en un test los
   mockea también, y la pantalla no sale a Supabase por un costado. */
export * from "./importaciones";
